import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import { match as MatchProps } from "react-router";
import React, { Dispatch, useEffect, useState } from "react";

import {
  Box,
  TextField,
  Button,
  Autocomplete,
  FormControl,
  FormControlLabel,
  Checkbox,
  Chip,
  Typography
} from "@mui/material";

import { DentalLabFormMode, DentalLabOrderStatus } from "../../../enum/DentalLabOrder";
import {
  getDentalLabOrder,
  patchDentalLabOrder,
  postDentalLabOrder
} from "../../../api/dentalLabOrder";
import {
  OCCLUSAL_STAIN_OPTIONS,
  PONTIC_DESIGNS,
  SHADE_OPTIONS,
  TEETH_NUMBERS,
  DENTAL_ITEMS
} from "../../../const/DentalLabOrder";

import Panel from "../../../components/Panel";
import { tl } from "../../../components/translate";
import ClientSearch from "../../../components/ClientSearch";
import { Client } from "../../../interfaces/ClientInterface";
import { notificationAdd } from "../../../actions/notification";
import { clientSearchSecondaryLabel } from "../../../models/Client";
import StatefulButton from "../../../components/StatefulButton/StatefulButton";
import CalendarDropdown from "../../../components/CalendarDropdown/CalendarDropdown";
import { DentalLabOrder, DentalLabOrderFormData } from "../../../interfaces/DentalLabOrder";
import ServiceProviderAutoSelect from "../../ServiceProvider/ServiceProviderAutoSelect";

interface DentalLabOrderFormProps {
  dentalLabOrder?: DentalLabOrder | null;
  mode: DentalLabFormMode;
  selectedClient?: Client;
  selectedServiceProviderId?: number;
  match: MatchProps<{ id?: string }>;
  onClose?: () => void;
  dentalLabOrderData?: { results: DentalLabOrder[]; total: number } | null;
  setDentalLabOrderData?: Dispatch<
    React.SetStateAction<{ results: DentalLabOrder[]; total: number } | null>
  >;
}

const allTeeth = [...TEETH_NUMBERS.TOP, ...TEETH_NUMBERS.BOTTOM];

const DentalLabOrderForm = ({
  dentalLabOrder,
  mode,
  match,
  onClose,
  selectedClient,
  selectedServiceProviderId,
  setDentalLabOrderData
}: DentalLabOrderFormProps): React.JSX.Element => {
  const dispatch = useDispatch();
  const dentalLabOrderId = Number(match.params?.id);
  const [loading, setLoading] = useState<boolean>(false);
  const [client, setClient] = useState<Client | null>(selectedClient || null);

  const [formData, setFormData] = useState<DentalLabOrderFormData>({
    clientId: selectedClient?.id || null,
    teeth: [],
    items: [],
    shadeDetails: [],
    occlusalStains: [],
    ponticDesigns: [],
    note: "",
    orderDate: new Date().toISOString(),
    estimatedDeliveryDate: new Date().toISOString(),
    status: DentalLabOrderStatus.ORDERED,
    serviceProviderId: selectedServiceProviderId || null
  });

  // helper functions
  const handleToothClick = (toothNumber: string) => {
    setFormData((prev) => {
      const isSelected = prev.teeth.includes(toothNumber as unknown as string);
      return {
        ...prev,
        teeth: isSelected
          ? prev.teeth.filter((t) => t !== (toothNumber as unknown as string))
          : [...prev.teeth, toothNumber as unknown as string]
      };
    });
  };

  const handleFullMouth = () => {
    setFormData((prev) => ({ ...prev, teeth: allTeeth.map((t) => t as unknown as string) }));
  };

  const handleClearTeeth = () => {
    setFormData((prev) => ({ ...prev, teeth: [] }));
  };

  const handleShadeToggle = (shade: string) => {
    setFormData((prev) => {
      const isSelected = prev.shadeDetails.includes(shade);
      return {
        ...prev,
        shadeDetails: isSelected
          ? prev.shadeDetails.filter((s: string) => s !== shade)
          : [...prev.shadeDetails, shade]
      };
    });
  };

  const handlePonticToggle = (design: string) => {
    setFormData((prev) => {
      const isSelected = prev.ponticDesigns.includes(design);
      return {
        ...prev,
        ponticDesigns: isSelected
          ? prev.ponticDesigns.filter((d: string) => d !== design)
          : [...prev.ponticDesigns, design]
      };
    });
  };

  const isFormFilled = () => {
    if (
      !client?.id ||
      !formData.serviceProviderId ||
      formData.teeth.length === 0 ||
      formData.items.length === 0 ||
      !formData.orderDate
    ) {
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!isFormFilled()) {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: "Please fill all the required fields (Client, Teeth, Items)",
          autoTimeout: true
        })
      );
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = {
        clientId: Number(client?.id || 0),
        teeth: formData.teeth,
        items: formData.items,
        shadeDetails: formData.shadeDetails,
        occlusalStains: formData.occlusalStains,
        ponticDesigns: formData.ponticDesigns,
        note: formData.note,
        orderDate: formData.orderDate,
        estimatedDeliveryDate: formData.estimatedDeliveryDate,
        status: formData.status,
        serviceProviderId: formData.serviceProviderId
      };

      if (mode === DentalLabFormMode.CREATE) {
        const { data: createdDentalLabOrder } = await postDentalLabOrder(data);
        if (!createdDentalLabOrder) {
          throw new Error("Failed to create dental lab order");
        }
        setDentalLabOrderData?.((prevData) => {
          if (!prevData) {
            return { results: [createdDentalLabOrder], total: 1 };
          }
          return {
            results: [createdDentalLabOrder, ...prevData.results],
            total: prevData.total + 1
          };
        });
        setLoading(false);
        dispatch(
          notificationAdd({
            id: new Date().getUTCMilliseconds(),
            variant: "success",
            message: "Dental Lab Order created successfully",
            autoTimeout: true
          })
        );
      } else {
        const { data: updatedDentalLabOrder } = await patchDentalLabOrder(
          match.params.id ?? "",
          data
        );
        if (!updatedDentalLabOrder) {
          throw new Error("Failed to update dental lab order");
        }
        setDentalLabOrderData?.((prevData) => {
          if (!prevData) {
            return { results: [updatedDentalLabOrder], total: 1 };
          }
          return {
            ...prevData,
            results: prevData.results.map((item) =>
              item.id === updatedDentalLabOrder?.id ? updatedDentalLabOrder : item
            )
          };
        });
        setLoading(false);
        dispatch(
          notificationAdd({
            id: new Date().getUTCMilliseconds(),
            variant: "success",
            message: "Dental Lab Order updated successfully",
            autoTimeout: true
          })
        );
      }

      dispatch(push("/assessment/dentalLabOrders"));
    } catch (err) {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: (err as Error).message || "Something went wrong.",
          autoTimeout: true
        })
      );
    } finally {
      setLoading(false);
    }
  };

  // effect
  useEffect(() => {
    let isMounted = true;

    const fetchAndSetData = async () => {
      if (!isMounted) return;

      if (dentalLabOrderId && dentalLabOrder) {
        setClient(dentalLabOrder.client);
        setFormData({
          clientId: dentalLabOrder.clientId,
          teeth: dentalLabOrder.teeth,
          items: dentalLabOrder.items,
          shadeDetails: dentalLabOrder.shadeDetails || [],
          occlusalStains: dentalLabOrder.occlusalStains || [],
          ponticDesigns: dentalLabOrder.ponticDesigns || [],
          note: dentalLabOrder.note,
          orderDate: dentalLabOrder.orderDate,
          estimatedDeliveryDate: dentalLabOrder.estimatedDeliveryDate,
          status: dentalLabOrder.status,
          serviceProviderId: dentalLabOrder.serviceProviderId
        });
      } else if (!dentalLabOrder && dentalLabOrderId) {
        try {
          const { data: fetchedDentalLabOrder } = await getDentalLabOrder(dentalLabOrderId);
          if (!fetchedDentalLabOrder) return;
          if (!isMounted) return;

          setClient(fetchedDentalLabOrder?.client);
          setFormData({
            clientId: fetchedDentalLabOrder?.clientId,
            teeth: fetchedDentalLabOrder?.teeth || [],
            items: fetchedDentalLabOrder?.items || [],
            shadeDetails: fetchedDentalLabOrder?.shadeDetails || [],
            occlusalStains: fetchedDentalLabOrder?.occlusalStains || [],
            ponticDesigns: fetchedDentalLabOrder?.ponticDesigns || [],
            note: fetchedDentalLabOrder?.note || "",
            orderDate: fetchedDentalLabOrder?.orderDate,
            estimatedDeliveryDate: fetchedDentalLabOrder?.estimatedDeliveryDate,
            status: fetchedDentalLabOrder?.status,
            serviceProviderId: fetchedDentalLabOrder.serviceProviderId
          });
        } catch (err) {
          if (!isMounted) return;

          dispatch(
            notificationAdd({
              id: new Date().getUTCMilliseconds(),
              variant: "error",
              message: (err as Error).message || "Failed to fetch dental lab order",
              autoTimeout: true
            })
          );
        }
      }
    };

    fetchAndSetData();

    return () => {
      isMounted = false;
    };
  }, [dentalLabOrder, dentalLabOrderId, dispatch]);

  const footer = (
    <Box
      style={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        justifyContent: "flex-end",
        gap: "10px"
      }}
    >
      <Button
        onClick={() => dispatch(push("/assessment/dentalLabOrders"))}
        data-testmation="cancelDentalLabOrderForm"
        style={{ height: "32px", width: "60px" }}
      >
        {tl("cancel")}
      </Button>
      <StatefulButton
        variant="contained"
        color="primary"
        disabled={loading || !isFormFilled()}
        onClick={handleSave}
        isLoading={loading}
        circularProgressProps={{ size: 16 }}
        data-testmation="createDentalLabOrder"
        style={{ height: "35px", width: "80px" }}
      >
        {mode === DentalLabFormMode.CREATE ? tl("save") : tl("update")}
      </StatefulButton>
    </Box>
  );

  return (
    <Panel
      title={
        mode === DentalLabFormMode.CREATE ? "Create Dental Lab Order" : "Edit Dental Lab Order"
      }
      onClose={() => {
        if (onClose) {
          onClose();
        } else {
          dispatch(push("/assessment/dentalLabOrders"));
        }
      }}
      footer={footer}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column"
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "15px"
          }}
        >
          {/* Client Search */}
          <ClientSearch
            data-testmation="clientSearch"
            autoFocus={!client}
            client={client}
            setClient={setClient}
            variant="outlined"
            margin="dense"
            label="Select Client *"
            placeholder="Client Name"
            style={{ width: "100%" }}
            isDisabled={loading}
            onChange={(value: Client | null) => {
              setClient(value);
            }}
            secondaryText={[
              { type: clientSearchSecondaryLabel.CUSTOMER_NUMBER },
              {
                type: clientSearchSecondaryLabel.EXTERNAL_IDENTIFIER
              }
            ]}
          />

          {/* Service Provider select */}
          <ServiceProviderAutoSelect
            hideLabel={false}
            serviceProviderId={Number(formData.serviceProviderId)}
            label="Service provider *"
            placeholder="Service provider"
            limitBy={[]}
            disabled={loading}
            isClearable={false}
            disableDefaultError
            onChange={(value) => {
              setFormData({ ...formData, serviceProviderId: Number(value) });
            }}
          />

          {/* Teeth Selection */}
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="subtitle2">Teeth Numbers (FDI Notation) *</Typography>
              <Box display="flex" gap={1} mb={1}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleFullMouth}
                  disabled={loading}
                >
                  Full Mouth
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleClearTeeth}
                  disabled={loading}
                >
                  Clear
                </Button>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                mb: 1,
                width: "100%",
                justifyContent: "center"
              }}
            >
              {TEETH_NUMBERS.TOP.map((tooth) => (
                <Button
                  key={tooth}
                  variant={formData.teeth.includes(tooth) ? "contained" : "outlined"}
                  size="small"
                  onClick={() => handleToothClick(tooth)}
                  disabled={loading}
                  sx={{
                    flex: "1 1 0",
                    minWidth: 0
                  }}
                >
                  {tooth}
                </Button>
              ))}
            </Box>

            {/* Bottom Row */}
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                width: "100%",
                justifyContent: "center"
              }}
            >
              {TEETH_NUMBERS.BOTTOM.map((tooth) => (
                <Button
                  key={tooth}
                  variant={formData.teeth.includes(tooth) ? "contained" : "outlined"}
                  size="small"
                  onClick={() => handleToothClick(tooth)}
                  disabled={loading}
                  sx={{
                    flex: "1 1 0",
                    minWidth: 0
                  }}
                >
                  {tooth}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Items */}
          <Box>
            <Typography variant="subtitle2">Items</Typography>
            <Autocomplete
              multiple
              fullWidth
              disabled={loading}
              options={[...DENTAL_ITEMS]}
              getOptionLabel={(option) => option}
              value={formData.items}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="dense"
                  variant="outlined"
                  placeholder="Select items"
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
                ))
              }
              onChange={(e, value) => {
                setFormData({ ...formData, items: value });
              }}
            />
          </Box>

          {/* Occlusal Stains */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Occlusal Stains
            </Typography>

            <Autocomplete
              multiple
              fullWidth
              disabled={loading}
              options={[...OCCLUSAL_STAIN_OPTIONS]}
              getOptionLabel={(option) => option}
              value={formData.occlusalStains}
              onChange={(e, value) => {
                setFormData({ ...formData, occlusalStains: value });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="dense"
                  variant="outlined"
                  placeholder="Select occlusal stains"
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
                ))
              }
            />
          </Box>

          {/* Shade Details */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Shade Details
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap"
              }}
            >
              {SHADE_OPTIONS.map((shade) => (
                <Button
                  key={shade}
                  variant={formData.shadeDetails.includes(shade) ? "contained" : "outlined"}
                  size="small"
                  onClick={() => handleShadeToggle(shade)}
                  disabled={loading}
                  sx={{
                    flex: "1 1 0",
                    minWidth: 0
                  }}
                >
                  {shade}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Pontic Design */}
          <FormControl>
            <Typography variant="subtitle2" gutterBottom>
              Pontic Designs
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr"
              }}
            >
              {PONTIC_DESIGNS.map((design) => (
                <FormControlLabel
                  key={design.value}
                  control={
                    <Checkbox
                      checked={formData.ponticDesigns.includes(design.value)}
                      onChange={() => handlePonticToggle(design.value)}
                      disabled={loading}
                      sx={{ py: 0.5 }}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {design.image && (
                        <Box
                          component="img"
                          src={design.image}
                          alt={design.value}
                          sx={{ width: 80, height: 80, borderRadius: 1 }}
                        />
                      )}
                      <Typography component="span">{design.value}</Typography>
                    </Box>
                  }
                />
              ))}
            </Box>
          </FormControl>

          {/* Note */}
          <TextField
            fullWidth
            multiline
            rows={4}
            value={formData.note}
            margin="dense"
            variant="outlined"
            label="Note"
            placeholder="Enter any additional note"
            disabled={loading}
            slotProps={{
              inputLabel: { shrink: true }
            }}
            onChange={(e) => {
              setFormData({ ...formData, note: e.target.value });
            }}
          />

          {/* Dates */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <CalendarDropdown
              fullwidth
              TextFieldProps={{ variant: "outlined" }}
              label="Order Date *"
              placeholder="Select order date"
              date={formData.orderDate ? new Date(formData.orderDate) : new Date()}
              onChange={(val) => {
                if (val) {
                  setFormData({ ...formData, orderDate: val.toISOString() });
                }
              }}
            />

            <CalendarDropdown
              fullwidth
              TextFieldProps={{ variant: "outlined" }}
              label="Estimated Delivery Date"
              placeholder="Select estimated delivery date"
              date={
                formData.estimatedDeliveryDate
                  ? new Date(formData.estimatedDeliveryDate)
                  : new Date()
              }
              onChange={(val) => {
                if (val) {
                  setFormData({ ...formData, estimatedDeliveryDate: val.toISOString() });
                }
              }}
            />
          </Box>
        </Box>
      </Box>
    </Panel>
  );
};

export default DentalLabOrderForm;
